import { useContext, useMemo, useEffect, useCallback } from 'react';
import InlineOptions from '../../inline-options';
import { VolCompareContext } from '../../../../providers/widgets';
import Chart from '../../chart';
import Divider from '../../divider';
import { SnapshotTypeEnum } from '../../../../model/snapshot_type';
import moment from 'moment';
import { showToast } from '../../../toast-notification';
import { LegendComponentOption } from 'echarts';
import DataCalendar from '../../../data-calendar';
import VolSeriesChip from '../../../series-chip/vol_series_chip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { lineChartColors } from '../../../../theme/colors';
import {
  VOL_SMILE_PERIOD_KEYS,
  VOL_SMILE_TENORS_KEYS,
} from '../../../../providers/widgets/volatility-smile/vol-smile.constants';
import './styles.scss';
import usePersistentState from '../../../../providers/use-persistent-state';
import TimestampInfo from '../../timestamp-info';

type Props = {
  isTermStructure?: boolean;
  providerKey: string;
};

const VolatilitySmileComparisonContent = ({ providerKey, isTermStructure = false }: Props) => {
  const {
    dataExpiry,
    setDataExpiry,
    dataTenor,
    setDataTenor,
    chips,
    tenor,
    setTenor,
    tenors,
    expiry,
    setExpiry,
    expiries,
    snapshotType,
    setSnapshotType,
    getData,
    setChips,
    timestamp,
  } = useContext(VolCompareContext);

  const [legendSelected, setLegendSelected] = usePersistentState<Record<string, boolean>>(
    [providerKey, 'VOL-COMPARISON', isTermStructure ? 'TERM-STRUCTURE' : ''],
    'legendSelected',
    {},
  );

  const data = useMemo(() => {
    return snapshotType === SnapshotTypeEnum.EE ? dataExpiry : dataTenor;
  }, [snapshotType, dataExpiry, dataTenor]);

  const setData = useCallback(
    (updater: React.SetStateAction<any>) => {
      if (snapshotType === SnapshotTypeEnum.EE) {
        setDataExpiry(updater);
      } else {
        setDataTenor(updater);
      }
    },
    [snapshotType, setDataExpiry, setDataTenor],
  );

  const limitDate = new Date();

  limitDate.setDate(limitDate.getDate() - 1);

  useEffect(() => {
    if (!chips || chips.length === 0) return;

    chips.forEach((chip) => {
      if (chip.key === 'live-data') return;
      if (chip.disabled) return;

      const displayName = chip.key === 'live-data' ? 'Live Data' : chip.key;
      const isSelected = legendSelected[displayName];
      if (!isSelected) return;

      let date = moment().utc();
      if (chip.key.startsWith('T-')) {
        const days = parseInt(chip.key.replace('T-', ''), 10);
        if (!Number.isNaN(days)) date.subtract(days, 'days');
      } else if (moment(chip.key).isValid()) {
        date = moment(chip.key).utc();
      }

      const dateString = date.toISOString();
      const onlyDate = dateString.substring(0, 10);

      if (!data || !data[onlyDate]) {
        getData(dateString, chip.key);
      }
    });
  }, [chips, data, getData, legendSelected]);

  useEffect(() => {
    if (isTermStructure) return;
    if (snapshotType !== SnapshotTypeEnum.EE) return;
    setChips((prev) => prev.map((chip) => ({ ...chip, disabled: false })));
  }, [expiry, snapshotType, setChips, isTermStructure]);

  useEffect(() => {
    if (isTermStructure) return;
    if (snapshotType !== SnapshotTypeEnum.EE) return;
    if (!data) return;

    setChips((prevChips) =>
      prevChips.map((chip) => {
        if (chip.key === 'live-data') return chip;

        let date = moment().utc();
        if (chip.key.startsWith('T-')) {
          const days = parseInt(chip.key.replace('T-', ''), 10);
          if (!Number.isNaN(days)) date.subtract(days, 'days');
        } else if (moment(chip.key).isValid()) {
          date = moment(chip.key).utc();
        }

        const onlyDate = date.toISOString().substring(0, 10);

        if ((data as any)[onlyDate] && !(data as any)[onlyDate][expiry]) {
          if (!chip.key.includes('T-') && !chip.disabled) {
            showToast({ message: 'Contract not available', type: 'error' });
          }
          return { ...chip, disabled: true };
        }

        return chip;
      }),
    );
  }, [expiry, data, snapshotType, setChips, isTermStructure, dataExpiry, dataTenor]);

  const handleAddDate = (date: string) => {
    const convertToDate = moment(date).utc().format('YYYY-MM-DD HH:mm [UTC]');

    const alreadyExists = chips.find((chip) => chip.key === convertToDate);

    if (!alreadyExists) {
      setLegendSelected((prev) =>
        prev[convertToDate] !== undefined ? prev : { ...prev, [convertToDate]: true },
      );

      setChips((prev) => {
        const arr = [
          ...prev,
          {
            key: convertToDate,
            color: lineChartColors[prev.length],
            visible: true,
            isRemovible: true,
            disabled: false,
          },
        ];

        return arr;
      });
    }
  };

  const renderHistoricChips = useMemo(() => {
    const handleDelete = (chipKey: string) => {
      setLegendSelected((prev) => {
        const next = { ...prev };
        delete next[chipKey === 'live-data' ? 'Live Data' : chipKey];
        return next;
      });

      setChips((prev) => prev.filter((c) => c.key !== chipKey));

      let date = moment(chipKey, 'YYYY-MM-DD HH:mm [UTC]', true).utc();
      if (!date.isValid()) date = moment(chipKey).utc();
      const onlyDate = date.toISOString().substring(0, 10);

      const stillUsesSameDay = chips
        .filter((c) => c.key !== chipKey)
        .some((c) => {
          if (c.key === 'live-data') {
            const today = moment().utc().toISOString().substring(0, 10);
            return today === onlyDate;
          }
          if (c.key.startsWith('T-')) {
            const d = moment()
              .utc()
              .subtract(parseInt(c.key.replace('T-', ''), 10) || 0, 'days');
            return d.toISOString().substring(0, 10) === onlyDate;
          }
          const d = moment(c.key, 'YYYY-MM-DD HH:mm [UTC]', true).utc().isValid()
            ? moment(c.key, 'YYYY-MM-DD HH:mm [UTC]', true).utc()
            : moment(c.key).utc();
          return d.toISOString().substring(0, 10) === onlyDate;
        });

      if (!stillUsesSameDay) {
        setData((prev: any) => {
          const newData = { ...prev };
          if (newData) delete newData[onlyDate];
          return newData;
        });
      }
    };

    return (
      <div
        className="volatility-smile-comparison-historic-chips"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}
      >
        {chips
          ?.filter((c) => c.isRemovible)
          .map((chip) => (
            <VolSeriesChip
              key={chip.key}
              series={{ label: chip.key, color: chip.color }}
              removable
              onDelete={() => handleDelete(chip.key)}
              disabled={snapshotType === SnapshotTypeEnum.EE ? !!chip.disabled : false}
            />
          ))}
      </div>
    );
  }, [chips, snapshotType, setChips, data, setData]);

  const renderInlineOptions = () => {
    return (
      <div className="volatility-smile-comparison-header">
        <div className="volatility-smile-comparison-header-left">
          <InlineOptions
            options={
              !isTermStructure
                ? [
                    { value: SnapshotTypeEnum.EE, label: 'Expiry' },
                    { value: SnapshotTypeEnum.CM, label: 'Tenor' },
                  ]
                : [{ value: SnapshotTypeEnum.CM, label: 'Tenor' }]
            }
            selectedOption={snapshotType}
            onOptionChange={(val) => setSnapshotType(val as SnapshotTypeEnum)}
          />
          <Divider />
          {isTermStructure ? (
            <InlineOptions
              options={tenors?.map((t) => ({ label: t, value: t }))}
              selectedOption={tenor}
              onOptionChange={(val) => setTenor(val as string)}
              maxVisibleOptions={10}
            />
          ) : snapshotType === SnapshotTypeEnum.EE ? (
            <InlineOptions
              options={expiries?.map((e) => ({ label: e, value: e }))}
              selectedOption={expiry}
              onOptionChange={(val) => setExpiry(val as string)}
              maxVisibleOptions={4}
            />
          ) : (
            <InlineOptions
              options={tenors?.map((t) => ({ label: t, value: t }))}
              selectedOption={tenor}
              onOptionChange={(val) => setTenor(val as string)}
              maxVisibleOptions={4}
            />
          )}

          <DataCalendar
            onChange={handleAddDate}
            limitDate={limitDate}
            button={
              <VolSeriesChip
                series={{ label: 'Add Historic Data', color: 'transparent' }}
                icon={<FontAwesomeIcon icon={faPlusCircle} />}
                classnames="volatility-smile-comparison-calendar"
              />
            }
          />
        </div>

        <div className="volatility-smile-comparison-header-right">
          <TimestampInfo timestamp={new Date(timestamp).getTime()} keepLocalTime />
        </div>
      </div>
    );
  };

  const series = useMemo(() => {
    if (!chips || chips.length === 0) return [];

    const emptySmileValues = new Array(VOL_SMILE_TENORS_KEYS.length).fill(undefined);
    const emptyTermValues = new Array(VOL_SMILE_PERIOD_KEYS.length).fill(undefined);

    return chips.map((chip) => {
      let date = moment().utc();
      if (chip.key.startsWith('T-')) {
        const days = parseInt(chip.key.replace('T-', ''), 10);
        if (!Number.isNaN(days)) date.subtract(days, 'days');
      } else if (chip.key !== 'live-data' && moment(chip.key).isValid()) {
        date = moment(chip.key).utc();
      }

      const storeKey = date.toISOString().substring(0, 10);
      const tenorsObj = (data as any)?.[storeKey];

      let values: (number | null)[] | undefined = isTermStructure
        ? emptyTermValues
        : emptySmileValues;

      if (tenorsObj) {
        if (isTermStructure) {
          values = VOL_SMILE_PERIOD_KEYS.map((k) => {
            const ts = tenorsObj?.[k];
            return ts ? (ts?.[tenor] ?? null) : null;
          });
        } else {
          if (snapshotType === SnapshotTypeEnum.EE) {
            const expObj = tenorsObj?.[expiry];
            values = expObj
              ? VOL_SMILE_TENORS_KEYS.map((k) => expObj?.[k] ?? null)
              : emptySmileValues;
          } else {
            const tenorObj = tenorsObj?.[tenor];
            values = tenorObj
              ? VOL_SMILE_TENORS_KEYS.map((k) => tenorObj?.[k] ?? null)
              : emptySmileValues;
          }
        }
      }

      const displayName = chip.key === 'live-data' ? 'Live Data' : chip.key;

      return {
        id: chip.key,
        type: 'line' as const,
        data: values,
        color: chip.color,
        name: displayName,
        key: chip.key,
        symbolSize: 6,
        symbol: 'circle',
        animation: false,
        animationDuration: 0,
        lineStyle: { type: 'solid' },
      };
    });
  }, [data, setData, chips, snapshotType, expiry, tenor, isTermStructure, VOL_SMILE_PERIOD_KEYS]);

  const legend = useMemo(() => {
    const names = (chips || []).map((c) => (c.key === 'live-data' ? 'Live Data' : c.key));

    const base: LegendComponentOption = {
      selectedMode: 'multiple' as const,
      data: names,
    };

    const selected: Record<string, boolean> = {};
    names.forEach((n) => {
      selected[n] =
        legendSelected[n] ?? (isTermStructure ? true : n === 'Live Data' || n === 'T-1');
    });

    return { ...base, selected };
  }, [chips, legendSelected, isTermStructure]);

  const { finalSeries, finalXAxisData } = useMemo(() => {
    const baseCategories = isTermStructure ? VOL_SMILE_PERIOD_KEYS : VOL_SMILE_TENORS_KEYS;

    if (!series || series.length === 0) {
      return { finalSeries: series, finalXAxisData: baseCategories };
    }

    const selection: Record<string, boolean> = legend?.selected || {};

    let lastIdx = baseCategories.length - 1;
    for (; lastIdx >= 0; lastIdx--) {
      const hasAnyVisibleValue = series.some((s: any) => {
        if (selection && selection[s.name] === false) return false;
        const val = s.data?.[lastIdx];
        return val != null && !Number.isNaN(val);
      });
      if (hasAnyVisibleValue) break;
    }

    if (lastIdx < baseCategories.length - 1 && lastIdx >= 0) {
      const trimmedCats = baseCategories.slice(0, lastIdx + 1);
      const trimmedSeries = series.map((s: any) => ({
        ...s,
        data: (s.data || []).slice(0, lastIdx + 1),
      }));
      return { finalSeries: trimmedSeries, finalXAxisData: trimmedCats };
    }

    return { finalSeries: series, finalXAxisData: baseCategories };
  }, [series, legend, isTermStructure]);

  const xAxis = useMemo(
    () => ({
      type: 'category' as const,
      data: finalXAxisData,
    }),
    [finalXAxisData],
  );

  useEffect(() => {
    if (Object.keys(legendSelected).length > 0) return;
    const names = (chips || []).map((c) => (c.key === 'live-data' ? 'Live Data' : c.key));
    const selected: Record<string, boolean> = {};
    names.forEach((n) => {
      selected[n] = isTermStructure ? true : n === 'Live Data' || n === 'T-1';
    });
    setLegendSelected(selected);
  }, [chips, legendSelected, isTermStructure]);

  const yAxis = useMemo(
    () => ({
      type: 'value' as const,
      axisLabel: {
        formatter: (value: any) => `${(value * 100).toFixed(0)}%`,
      },
    }),
    [],
  );

  const sanitalizedSeries = finalSeries.map((s) => {
    return { ...s, data: s.data.filter((d: any) => d !== null && d !== undefined) };
  });

  return (
    <div className="volatility-smile-comparison-container">
      {renderInlineOptions()}
      {renderHistoricChips}
      <Chart
        series={sanitalizedSeries}
        xAxis={xAxis}
        yAxis={yAxis}
        merge={false}
        grid={{
          bottom: '40px',
          top: '20px',
        }}
        legend={{
          ...legend,
        }}
        onLegendSelectChanged={(payload) => setLegendSelected(payload.selected || {})}
        tooltip={{
          valueFormatter: (value: any) => {
            const num = typeof value === 'number' ? value : Number(value);
            if (value == null || Number.isNaN(num)) return '-';
            return `${(num * 100).toFixed(2)}%`;
          },
        }}
        className="vol-smile-comparison-chart"
        isLoading={!data}
      />
    </div>
  );
};

export default VolatilitySmileComparisonContent;
